import { Array2D, Map2D, MapND, mod } from "@kosmod/kit";
import { CanvasID } from "./canvas";
import { TerrainCanvasID } from "@kosmod/theatre";

/**
 * A NDMap-compatible key for any chunk
 */
export type ChunkKey = [canvas: CanvasID, x: number, y: number]


export class Set2D<KeyX, KeyY> {
  #inner = new Map<KeyY, Set<KeyX>>();

  constructor(initial: [KeyX, KeyY][] = []) {
    for (let [x, y] of initial) {
      const set = this.#inner.get(y) || new Set();
      set.add(x);
      this.#inner.set(y, set);
    }
  }

  add(x: KeyX, y: KeyY) {
    const set = this.#inner.get(y) || new Set();
    set.add(x);
    this.#inner.set(y, set);
  }
  delete(x: KeyX, y: KeyY) {
    const set = this.#inner.get(y) || new Set();
    set.delete(x);
    if (set.size === 0) {
      this.#inner.delete(y)
    }
  }
  has(x: KeyX, y: KeyY) {
    const inner = this.#inner.get(y);
    return !!inner && inner.has(x);
  }

  *keys(): Iterable<[KeyX, KeyY]> {
    for (let [y, set] of this.#inner) {
      for (let x of set)
        yield [x, y];
    }
  }

  *[Symbol.iterator](): Iterator<[KeyX, KeyY]> {
    for (const key of this.keys())
      yield key;
  }
}

/**
 * Rich Chunks are "hydrated" chunks, there
 * the data is stored in an uncompressed format.
 * 
 * Their counterpart is a CompressedChunk
 */
export type RichChunk<T> = {
  canvas: CanvasID,
  x: number,
  y: number,
  data: Array2D<T>,
};

export type ReadonlyChunkMap<T> = {
  canvasId: TerrainCanvasID,

  chunks: {
    keys: Map2D<number, number, RichChunk<T>>["keys"]
  },

  read(x: number, y: number): T,
}


export type ChunkMap<T> = {
  canvasId: TerrainCanvasID,

  chunks: Map2D<number, number, RichChunk<T>>,

  /**
   * If a chunk doesn't already exist, generate it and fill it with
   * values given from the "emptyRead" function.
   */
  gen(
    chunkX: number,
    chunkY: number,
  ): RichChunk<T>,

  write(x: number, y: number, value: T): void,
  read(x: number, y: number): T,
  readWrite(x: number, y: number, updater: (prev: T) => T): void,
}

export const createChunkMap = <T>(
  canvasId: TerrainCanvasID,
  emptyRead: (x: number, y: number) => T,
  options: { genOnEmpty?: boolean } = {}
): ChunkMap<T> => {
  const chunks = new Map2D<number, number, RichChunk<T>>();

  const gen = (chunkX: number, chunkY: number) => {
    const chunk = chunks.getOrNull(chunkX, chunkY);
    if (!chunk) {
      const data = new Array(64);

      for (let y = 0; y < 8; y++) {
        for (let x = 0; x < 8; x++) {
          const i = (y * 8) + x;
          data[i] = emptyRead(x + (chunkX * 8), y + (chunkY * 8));
        }
      }

      const newChunk = {
        x: chunkX,
        y: chunkY,
        canvas: canvasId,
        data: new Array2D(8, 8, data),
      };

      chunks.set(chunkX, chunkY, newChunk)
      return newChunk;
    }
    return chunk;
  };

  const read = (x: number, y: number) => {
    const chunkX = Math.floor(x / 8);
    const chunkY = Math.floor(y / 8);

    let chunk = chunks.getOrNull(chunkX, chunkY);

    if (!chunk && options.genOnEmpty) {
      chunk = gen(chunkX, chunkY)
    }

    if (chunk) {
      const localX = mod(x, 8);
      const localY = mod(y, 8);
      return chunk.data.get(localX, localY);
    } else {
      return emptyRead(x, y);
    }
  };
  const write = (x: number, y: number, value: T) => {
    return readWrite(x, y, () => value);
  }

  const readWrite = (x: number, y: number, updater: (prev: T) => T) => {
    const chunkX = Math.floor(x / 8);
    const chunkY = Math.floor(y / 8);

    let chunk = chunks.getOrNull(chunkX, chunkY);

    if (!chunk) {
      chunk = gen(chunkX, chunkY)
    }
    const localX = mod(x, 8);
    const localY = mod(y, 8);
    const prev = chunk.data.get(localX, localY);
    const value = updater(prev);
    chunk.data.set(localX, localY, value);
    return value;
  }

  return {
    canvasId,
    chunks,

    gen,
    read,
    write,
    readWrite,
  }
}

/**
 * Special chunk that encodes tile values with special
 * formats to save space/transmission
 */
export type CompressedChunk = {
  canvas: CanvasID,
  x: number,
  y: number,
  data:
    // each tile gets its own floating point number
    | { type: 'doubles', values: number[] & { length: 64 } }
    // a pair of 32-bit numbers, where each bit corresponds to a
    // value in the tile
    | { type: 'bitfield', a: number, b: number }
    // each tile get its own byte (0-255)
    | { type: 'bytes', values: number[] & { length: 16 } }
}